import { useRef, useMemo, useCallback, useEffect, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import type { MediaClass } from '@/types/api'
import type { ImageSource } from '@/types/openseadragon'
import { AnnotationsProvider } from '@/contexts/AnnotationsContext'
import type { AdvancedImageViewerRef } from '@/components/AdvancedImageViewer'
import { ZPlaneChips, type ZPlane } from '@/components/ZPlaneChips'
import { GroundTruthBar } from '@/components/GroundTruthBar'
import { UndoToast } from '@/components/UndoToast'
import { trpc } from '@/lib/trpc'
import '@annotorious/react/annotorious-react.css'

// Lazy load the viewer to avoid bundle bloat
import { lazy, Suspense } from 'react'
const AdvancedImageViewer = lazy(() => import('@/components/AdvancedImageViewer'))

/**
 * Main media viewer page component
 */
export function MediaViewerPage() {
  const { id: skillId, mediaId } = useParams<{ id: string; mediaId: string }>()
  const navigate = useNavigate()
  // Deep link: ?media_class=<id> selects and zooms to that box on load.
  const [searchParams] = useSearchParams()
  const deepLinkBoxId = searchParams.get('media_class')
  // ?filters=gt,rejection,suggestion — initial category visibility (grid chips).
  const filtersParam = searchParams.get('filters')
  // Drawing mode hides the page-level chrome (z-chips, GT bar) along with the
  // viewer's own floating panels.
  const [isDrawing, setIsDrawing] = useState(false)
  const viewerRef = useRef<AdvancedImageViewerRef>(null)
  const [selectedAnnotationId, setSelectedAnnotationId] = useState<string | null>(null)

  // Fetch skill
  const { data: skill, isLoading: skillLoading, error: skillError } = trpc.skills.getById.useQuery(
    { id: skillId! },
    { enabled: !!skillId, staleTime: 5 * 60 * 1000 }
  )

  // Fetch medias
  const { data: medias = [], isLoading: mediasLoading } = trpc.media.getByScopeId.useQuery(
    { scopeId: skill?.scope_id! },
    { enabled: !!skill?.scope_id, staleTime: 5 * 60 * 1000 }
  )

  // Fetch annotations for this skill's ground truth. The endpoint is SKILL-wide
  // (every media's rows); filter to the current media below or the right rail
  // renders other slides' boxes as crops of THIS slide's pixels.
  const { data: skillGroundTruth = [] } = trpc.mediaClasses.getGroundTruth.useQuery(
    { skillId: skillId! },
    { enabled: !!skillId, staleTime: 5 * 60 * 1000 }
  )

  // Find current media
  const currentMedia = medias.find((m) => m.id === mediaId) || medias[0]

  // Ground truth scoped to the slide on screen
  const annotations = useMemo(
    () => skillGroundTruth.filter((a) => a.media_id === currentMedia?.id),
    [skillGroundTruth, currentMedia?.id],
  )

  // Focal planes: multi-plane WSI media have one media_version per Z depth,
  // named .../Z{n}.{ext}. Single-plane media yield no planes (chips hidden).
  const [selectedZ, setSelectedZ] = useState<number | null>(null)
  useEffect(() => setSelectedZ(null), [currentMedia?.id]) // reset on media switch
  const zPlanes = useMemo(() => {
    const planes: { z: number; mediaVersionId: string; width: number; height: number }[] = []
    for (const mv of currentMedia?.media_versions ?? []) {
      const match = /Z(\d+)\.[^.]+$/.exec(mv.media_path ?? '')
      if (match) planes.push({ z: Number(match[1]), mediaVersionId: mv.id, width: mv.width, height: mv.height })
    }
    return planes.sort((a, b) => a.z - b.z)
  }, [currentMedia])
  const selectedPlane = zPlanes.find((p) => p.z === selectedZ)

  const displayedVersion = selectedPlane
    ? { id: selectedPlane.mediaVersionId, width: selectedPlane.width, height: selectedPlane.height }
    : {
        id: currentMedia?.media_versions?.[0]?.id,
        width: currentMedia?.media_versions?.[0]?.width,
        height: currentMedia?.media_versions?.[0]?.height,
      }
  const imageWidth = displayedVersion.width || 3072
  const imageHeight = displayedVersion.height || 3072
  const mediaVersionId = displayedVersion.id

  // Bbox-semantics rows (ground truth / rejections / suggestions) for the slide.
  // The old VSI shape layer (point / circle / roi) is retired: circles live on as
  // ground-truth boxes here, and points/ROIs are not of interest to the client.
  const { data: boxAnnotations = [] } = trpc.annotations.getBoxesByMedia.useQuery(
    { mediaId: currentMedia?.id! },
    { enabled: !!currentMedia?.id, staleTime: 5 * 60 * 1000 },
  )

  // Per-plane annotation counts for the chips (circle-derived boxes carry a
  // zIndex; plain bbox rows are plane-independent and only count under 'All').
  const planeChips: ZPlane[] = useMemo(
    () =>
      zPlanes.map((p) => ({
        z: p.z,
        mediaVersionId: p.mediaVersionId,
        count: boxAnnotations.filter((a) => a.zIndex === p.z).length,
      })),
    [zPlanes, boxAnnotations],
  )

  // Calculate navigator dimensions
  const NAVIGATOR_WIDTH = 156
  const aspectRatio = imageWidth / imageHeight
  const navigatorHeight = Math.round(NAVIGATOR_WIDTH / aspectRatio)

  // Image sources for OpenSeadragon
  const imageSources: ImageSource[] = useMemo(
    () => [
      {
        type: 'dzi',
        url: `/data/${skill?.organization_id}/${mediaVersionId}/image.dzi`,
        title: 'Media Viewer',
      },
    ],
    [skill?.organization_id, mediaVersionId],
  )

  const handleThumbnailClick = useCallback((item: MediaClass) => {
    const bounds = { x1: item.bbox_x1, y1: item.bbox_y1, x2: item.bbox_x2, y2: item.bbox_y2 }
    viewerRef.current?.zoomToBounds(bounds)
    setSelectedAnnotationId(item.id)
  }, [])

  // Show selected annotation on the main viewer
  useEffect(() => {
    if (!selectedAnnotationId || !annotations) {
      viewerRef.current?.showAnnotation(null)
      return
    }

    const selectedAnnotation = annotations.find((ann) => ann.id === selectedAnnotationId)

    if (selectedAnnotation && viewerRef.current) {
      viewerRef.current.showAnnotation({
        id: selectedAnnotation.id,
        x1: selectedAnnotation.bbox_x1,
        y1: selectedAnnotation.bbox_y1,
        x2: selectedAnnotation.bbox_x2,
        y2: selectedAnnotation.bbox_y2,
      })
    }
  }, [selectedAnnotationId, annotations])

  // Loading state
  if (skillLoading || mediasLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface-primary">
        <div className="h-16 w-16 animate-spin rounded-full border-t-4 border-fill-primary" />
      </div>
    )
  }

  // Error state
  if (skillError || !skill || !currentMedia) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface-primary">
        <div className="text-center">
          <h1 className="text-h1 mb-4 text-fill-destructive">Media not found</h1>
          <p className="text-body-default text-text-secondary">The requested media could not be loaded.</p>
        </div>
      </div>
    )
  }

  return (
    <AnnotationsProvider
      mediaId={mediaId!}
      classId={skill.class_id}
      organizationId={skill.organization_id}
      skillId={skillId}
    >
      <div className="fixed inset-0 overflow-hidden bg-surface-primary">
        <Suspense
          fallback={
            <div className="flex h-full items-center justify-center">
              <div className="h-16 w-16 animate-spin rounded-full border-t-4 border-fill-primary" />
            </div>
          }
        >
          <AdvancedImageViewer
            ref={viewerRef}
            imageSources={imageSources}
            showControls={true}
            showTitle={false}
            height="100%"
            enableAnnotations={true}
            imageWidth={imageWidth}
            imageHeight={imageHeight}
            showNavigator={true}
            navigatorWidth={NAVIGATOR_WIDTH}
            navigatorHeight={navigatorHeight}
            // Pass data for floating panels
            medias={medias}
            currentMediaId={mediaId!}
            organizationId={skill.organization_id}
            onMediaSelect={(selectedMediaId) => {
              navigate(`/skills/${skillId}/${selectedMediaId}`)
            }}
            annotations={annotations}
            onAnnotationClick={handleThumbnailClick}
            scopeName={skill.scope_name}
            classLabel={skill.class_name}
            boxAnnotations={boxAnnotations}
            visibleZIndex={selectedZ}
            deepLinkBoxId={deepLinkBoxId}
            filtersParam={filtersParam}
            onDrawingModeChange={setIsDrawing}
          />
        </Suspense>

        {/* Focal-plane chips — floating top-center over the viewer */}
        <div
          className="pointer-events-none absolute left-1/2 z-40 -translate-x-1/2 transition-[opacity,top] duration-300"
          style={{ top: 'calc(var(--header-h, 80px) + 1rem)', opacity: isDrawing ? 0 : 1 }}
        >
          <ZPlaneChips
            planes={planeChips}
            selectedZ={selectedZ}
            totalCount={boxAnnotations.length}
            onSelect={setSelectedZ}
          />
        </div>

        {/* Ground-truth strip in the header (rc-app parity): the skill's
            confirmed positives from every slide; click deep-links to one. */}
        <GroundTruthBar
          rows={skillGroundTruth}
          medias={medias}
          organizationId={skill.organization_id}
          hidden={isDrawing}
          onItemClick={(row) =>
            navigate(`/skills/${skillId}/${row.media_id}?media_class=${row.id}`)
          }
        />

        {/* Post-action undo toast (annotation saves / suggestion resolutions) */}
        <UndoToast />
      </div>
    </AnnotationsProvider>
  )
}
